import { defaultComponentThemes } from '@backstage/theme';

// `createUnifiedTheme` (node_modules/@backstage/theme/dist/unified/
// UnifiedTheme.esm.js) faz `{ ...defaultComponentThemes, ...options.components }`
// — um merge RASO por nome de componente. Se a gente passasse `MuiButton`
// direto aqui, perderia o override default do Backstage pra esse
// componente (ex.: MuiChip perderia spacing/fontWeight, MuiTableCell
// perderia a densidade padrão). Por isso mescla um nível mais pra dentro,
// em `styleOverrides`, em vez de substituir a chave inteira.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StyleOverrides = Record<string, any>;

function mergeStyleOverrides(
  base: StyleOverrides | undefined,
  extra: StyleOverrides,
): StyleOverrides {
  const merged: StyleOverrides = { ...base };
  for (const key of Object.keys(extra)) {
    const baseValue = merged[key];
    const extraValue = extra[key];
    if (typeof baseValue === 'function' || typeof extraValue === 'function') {
      merged[key] = (props: unknown) => ({
        ...(typeof baseValue === 'function' ? baseValue(props) : baseValue),
        ...(typeof extraValue === 'function' ? extraValue(props) : extraValue),
      });
    } else {
      merged[key] = { ...baseValue, ...extraValue };
    }
  }
  return merged;
}

// Import só como valor: sem `?? {}` o TS marca a leitura como possivelmente
// `undefined` (o tipo declarado é `ThemeOptions['components']`, que é
// opcional na interface de origem, mesmo a const sempre existindo em
// runtime).
const base = defaultComponentThemes ?? {};

type Mode = 'light' | 'dark';

// shape.borderRadius global não existe em UnifiedThemeOptions nesta versão
// do @backstage/theme (0.7.3) — o createBaseThemeOptions só repassa
// palette/typography/page/getPageTheme pro createTheme do MUI, ignorando
// qualquer outra chave. Em vez de brigar com isso, os raios ficam
// hardcoded por componente (10 em Paper/Card, 8 em Button, 6 em Chip),
// que aliás é exatamente o que o brief pediu valor a valor.
export function buildComponentOverrides(mode: Mode) {
  const paperBorder = mode === 'light' ? '#d3d2d2' : '#3a3535';
  const paperShadow =
    mode === 'light' ? '0 1px 2px rgba(26, 23, 23, 0.06)' : 'none';

  return {
    MuiButton: {
      ...base.MuiButton,
      styleOverrides: mergeStyleOverrides(
        base.MuiButton?.styleOverrides as StyleOverrides,
        {
          root: { borderRadius: 8, fontWeight: 600, textTransform: 'none' },
        },
      ),
    },
    MuiChip: {
      ...base.MuiChip,
      styleOverrides: mergeStyleOverrides(
        base.MuiChip?.styleOverrides as StyleOverrides,
        {
          root: { borderRadius: 6 },
          label: {
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.75rem',
          },
        },
      ),
    },
    MuiPaper: {
      ...base.MuiPaper,
      styleOverrides: mergeStyleOverrides(
        base.MuiPaper?.styleOverrides as StyleOverrides,
        {
          root: {
            border: `1px solid ${paperBorder}`,
            borderRadius: 10,
            boxShadow: paperShadow,
          },
        },
      ),
    },
    MuiCard: {
      ...base.MuiCard,
      styleOverrides: mergeStyleOverrides(
        base.MuiCard?.styleOverrides as StyleOverrides,
        {
          root: {
            border: `1px solid ${paperBorder}`,
            borderRadius: 10,
            boxShadow: paperShadow,
          },
        },
      ),
    },
    MuiTableCell: {
      ...base.MuiTableCell,
      styleOverrides: mergeStyleOverrides(
        base.MuiTableCell
          ?.styleOverrides as StyleOverrides,
        {
          root: { padding: '12px 16px' },
          head: {
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            fontSize: '0.72rem',
          },
        },
      ),
    },
    // Header de página (Header.tsx do core-components, makeStyles com
    // `{ name: 'BackstageHeader' }`) — único jeito de tocar title/subtitle
    // sem forkar o componente: @backstage/core-components já declara
    // `OverrideComponentNameToClassKeys.BackstageHeader` em '@backstage/theme',
    // então o mesmo mecanismo shallow-merge do createUnifiedTheme funciona
    // aqui igual a qualquer MuiXxx. Peso/família do título já vem do
    // typography.h1 global; só falta opacity/weight do subtitle.
    BackstageHeader: {
      styleOverrides: {
        subtitle: { opacity: 0.7, fontWeight: 400 },
      },
    },
  };
}
